/**
 * EmptyState — componente reutilizavel para estados vazios/erro/sem-permissao.
 *
 * Substitui o uso solto de `<PageState>Sem dados</PageState>` ou
 * `Erro: Failed to fetch` que apareciam pelo app. Empty states orientam:
 * dizem POR QUE esta vazio e O QUE FAZER agora.
 *
 * Variantes (`tone`):
 *   - "ok"      verde/sucesso       — "Tudo em dia"
 *   - "search"  azul/info           — "Nenhum resultado de busca"
 *   - "error"   vermelho/danger     — "Falha de conexao", "Sem planilha"
 *   - "locked"  cinza/restrito      — "Sem acesso a este modulo"
 *   - "info"    cinza neutro default
 *
 * Uso:
 *
 *   <EmptyState
 *     tone="error"
 *     titulo="Sem planilha carregada"
 *     descricao="Carregue a planilha do Gerenciador para visualizar os dados."
 *     acoes={[
 *       { label: "Carregar planilha", onClick: () => navigate("/inicio") },
 *     ]}
 *   />
 *
 * Multiplas cartas em grid (como na referencia visual):
 *
 *   <EmptyStateGrid>
 *     <EmptyState tone="ok" titulo="..." />
 *     <EmptyState tone="search" titulo="..." />
 *     <EmptyState tone="locked" titulo="..." />
 *   </EmptyStateGrid>
 */

import React from "react";
import styled from "styled-components";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiInbox,
  FiLock,
  FiSearch,
  FiWifiOff,
} from "react-icons/fi";

const ICONES_POR_TOM = {
  ok: FiCheckCircle,
  search: FiSearch,
  error: FiWifiOff,
  locked: FiLock,
  info: FiInbox,
  warn: FiAlertCircle,
};

const CORES_POR_TOM = {
  ok: "success",
  search: "info",
  error: "danger",
  locked: "textSecondary",
  info: "textSecondary",
  warn: "warning",
};

export function EmptyState({
  tone = "info",
  titulo,
  descricao,
  acoes = [],
  icone: IconeProp,
  children,
}) {
  const IconeDefault = ICONES_POR_TOM[tone] || FiInbox;
  const Icone = IconeProp || IconeDefault;
  const corChave = CORES_POR_TOM[tone] || "textSecondary";

  return (
    <Card $tone={tone}>
      <IconeWrap $corChave={corChave}>
        <Icone size={32} />
      </IconeWrap>
      {titulo && <Titulo>{titulo}</Titulo>}
      {descricao && <Descricao>{descricao}</Descricao>}
      {children}
      {acoes.length > 0 && (
        <Acoes>
          {acoes.map((acao, i) => (
            <BotaoAcao
              key={acao.label || i}
              type="button"
              onClick={acao.onClick}
              $variant={i === acoes.length - 1 ? "primary" : "secondary"}
              disabled={acao.disabled}
            >
              {acao.label}
            </BotaoAcao>
          ))}
        </Acoes>
      )}
    </Card>
  );
}

export const EmptyStateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  padding: 20px 0;
`;

/**
 * Helper para o caso comum "erro ao carregar dados" — substitui o pattern
 * `<PageState $tone="error">Erro: {erro}</PageState>` espalhado pelas paginas
 * legadas. Mostra a mensagem do erro + botao "Tentar novamente" se passado.
 *
 *   {erro && <EmptyStateErro erro={erro} onRetry={recarregarDados} />}
 */
export function EmptyStateErro({ erro, onRetry, titulo }) {
  const msg = typeof erro === "string" ? erro : erro?.message || "Falha desconhecida";
  const ehFetchFail = /failed to fetch|networkerror|fetch/i.test(msg);
  return (
    <EmptyState
      tone="error"
      titulo={titulo || (ehFetchFail ? "Sem conexão com os dados" : "Não foi possível carregar")}
      descricao={
        ehFetchFail
          ? "Não foi possível acessar a planilha ou o servidor. Verifique sua conexão e tente novamente."
          : msg
      }
      acoes={onRetry ? [{ label: "Tentar novamente", onClick: onRetry }] : []}
    />
  );
}

/**
 * Helper para "sem planilha / sem dados". Convida o usuario a carregar a
 * planilha. Por default vai pra /inicio (tela de selecao de planilha do
 * PharuERP legado).
 *
 *   {!dados.length && <EmptyStateSemPlanilha onSelectPlanilha={() => navigate("/inicio")} />}
 */
export function EmptyStateSemPlanilha({ onSelectPlanilha, titulo, descricao }) {
  return (
    <EmptyState
      tone="info"
      icone={FiInbox}
      titulo={titulo || "Sem dados para exibir"}
      descricao={
        descricao
        || "Carregue a planilha do Gerenciador no menu início para visualizar os ambientes."
      }
      acoes={
        onSelectPlanilha
          ? [{ label: "Carregar planilha", onClick: onSelectPlanilha }]
          : []
      }
    />
  );
}

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 12px;
  padding: 40px 24px;
  min-height: 240px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 14px;
`;

const IconeWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  color: ${({ theme, $corChave }) => theme[$corChave] || theme.textSecondary};
  margin-bottom: 4px;

  /* "12% da cor do tom" SEM color-mix: o html2canvas (export JPG/PNG) não
     parseia o color(srgb ...) que o color-mix computa — um véu currentColor
     com opacity produz o mesmo visual e é capturável. */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.12;
  }
`;

const Titulo = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
`;

const Descricao = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.textSecondary};
  max-width: 380px;
`;

const Acoes = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
  justify-content: center;
`;

const BotaoAcao = styled.button`
  appearance: none;
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === "primary" ? theme.accent : theme.border};
  background: ${({ theme, $variant }) =>
    $variant === "primary" ? theme.accent : "transparent"};
  color: ${({ theme, $variant }) =>
    $variant === "primary"
      ? theme.accentText || "#fff"
      : theme.textPrimary};
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.18s ease, background 0.18s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default EmptyState;
